import { type DataTableColumn } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  IntegrationStatusProgress,
  SpinnerIcon
} from "../components/integration-status-progress";
import type { IntegrationProduct } from "../dtos/get-integration-product-list-response-dto";

const route = getRouteApi("/products/integration");

type IntegrationClickHandler = (data: {
  lampu_product_id: string;
  product_name: string;
}) => void;

type UseIntegrationProductListTableColumnsProps = {
  onCrmClick?: IntegrationClickHandler;
  onBclClick?: IntegrationClickHandler;
  onErfinaClick?: IntegrationClickHandler;
};

export const useIntegrationProductListTableColumns = ({
  onCrmClick,
  onBclClick,
  onErfinaClick
}: UseIntegrationProductListTableColumnsProps = {}) => {
  const { page } = route.useSearch();
  const { t } = useTranslation("product");

  const integrationProductListTableColumns = useMemo<
    DataTableColumn<IntegrationProduct>[]
  >(
    () => [
      {
        accessor: "no",
        title: t("integrationProductListPage.table.columns.no"),
        width: 60,
        render: (_, index) => (page - 1) * 10 + index + 1
      },
      {
        accessor: "lampu_product_id",
        title: t("integrationProductListPage.table.columns.productId"),
        render: ({ lampu_product_id }) => lampu_product_id ?? "-"
      },
      {
        accessor: "product_name",
        title: t("integrationProductListPage.table.columns.productName"),
        render: ({ product_name }) => product_name ?? "-"
      },
      {
        accessor: "crm_percentage",
        title: t("integrationProductListPage.table.columns.crm"),
        render: ({ crm_percentage, lampu_product_id, product_name }) => (
          <IntegrationStatusProgress
            percentage={crm_percentage}
            onClick={() => onCrmClick?.({ lampu_product_id, product_name })}
          />
        )
      },
      {
        accessor: "bcl_percentage",
        title: t("integrationProductListPage.table.columns.bcl"),
        render: ({ bcl_percentage, lampu_product_id, product_name }) => (
          <IntegrationStatusProgress
            percentage={bcl_percentage}
            onClick={() => onBclClick?.({ lampu_product_id, product_name })}
          />
        )
      },
      {
        accessor: "erfina_percentage",
        title: t("integrationProductListPage.table.columns.erfina"),
        render: ({ erfina_percentage, lampu_product_id, product_name }) => (
          <IntegrationStatusProgress
            percentage={erfina_percentage}
            onClick={() => onErfinaClick?.({ lampu_product_id, product_name })}
          />
        )
      },
      {
        accessor: "status",
        title: t("integrationProductListPage.table.columns.status"),
        render: ({
          status,
          crm_percentage,
          bcl_percentage,
          erfina_percentage
        }) => {
          const isIntegrated = status === "integrated";
          const totalPercentage =
            ((crm_percentage ?? 0) +
              (bcl_percentage ?? 0) +
              (erfina_percentage ?? 0)) /
            3;

          return (
            <div className="flex items-center gap-2">
              <span
                className={isIntegrated ? "text-[#34C759]" : "text-[#FFB800]"}
              >
                {isIntegrated
                  ? t("integrationProductListPage.table.status.integration")
                  : t("integrationProductListPage.table.status.incomplete")}
              </span>

              {isIntegrated && (
                <CheckIcon className="text-[#34C759] shrink-0" />
              )}
              {!isIntegrated && (
                <SpinnerIcon
                  percentage={totalPercentage}
                  className="text-[#FFCC00] shrink-0"
                />
              )}
            </div>
          );
        }
      }
    ],
    [t, page, onCrmClick, onBclClick, onErfinaClick]
  );

  return integrationProductListTableColumns;
};
